/**
 * Product Entity Schemas
 *
 * Mirrors the Prisma `Product` model + `ProductAudience` enum
 * (apps/backend/prisma/schema/base.prisma). A Product is the top of the
 * catalog: it owns Plans and AddOns. Used by the super-admin catalog UI and
 * any client consuming the catalog endpoints (plan #004, U5/U8).
 *
 * `publicId` is exposed as `id`; raw BigInt internal ids are never surfaced.
 */

import { z } from 'zod'

/**
 * Product audience. Mirrors the `ProductAudience` Prisma enum.
 * B2B is exercised this phase; B2C/B2B2C are modeled but not yet offered in
 * the catalog UI (B2B2C is hidden from the admin dropdown until the resolver
 * gains conjunctive-precondition handling — plan #004 OQ11).
 */
export const ProductAudienceSchema = z.enum(['B2B', 'B2C', 'B2B2C'])
export type ProductAudience = z.infer<typeof ProductAudienceSchema>

export const ProductSchema = z.object({
  id: z.string().describe('Product public ID (cuid)'),
  slug: z.string().describe('Stable product identifier'),
  name: z.string().describe('Product display name'),
  description: z.string().nullable().describe('Product description'),
  audience: ProductAudienceSchema.describe('Who the product is sold to'),
  isActive: z.boolean().describe('Whether the product is offered'),
  createdAt: z.string().describe('ISO timestamp'),
  updatedAt: z.string().nullable().describe('ISO timestamp')
})

export type Product = z.infer<typeof ProductSchema>

/**
 * Minimal product reference for nesting inside subscriptions / list views.
 */
export const ProductSummarySchema = ProductSchema.pick({
  id: true,
  slug: true,
  name: true,
  audience: true
})

export type ProductSummary = z.infer<typeof ProductSummarySchema>
